import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Alert, Grid,
  IconButton, InputAdornment, Tabs, Tab, Divider,
  Chip,
} from '@mui/material';
import {
  Send as SendIcon,
  Settings as SettingsIcon,
  Visibility,
  VisibilityOff,
  Save as SaveIcon
} from '@mui/icons-material';
import api from '../api';
import { navyBlue } from '../constants/colors';
import { useThemeColors } from '../hooks/useThemeColors';

export default function EmailSetup() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const { borderColor, tableRowHover, accentBlue, gradientPrimary, gradientPrimaryHover } = useThemeColors();

  const [settings, setSettings] = useState({
    provider: 'smtp',
    // SMTP settings
    host: '',
    port: '587',
    user: '',
    password: '',
    fromName: 'Sleeptronic Maintenance',
    // Resend settings
    resendApiKey: '',
    resendFrom: 'Sleeptronic Maintenance <onboarding@resend.dev>',
    // General
    appUrl: ''
  });

  const [testEmail, setTestEmail] = useState({
    to: '',
    subject: 'Test Email from Sleeptronic Maintenance',
    message: ''
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await api.get('/email-settings');
      setSettings(response.data);
    } catch (err) {
      console.error('Failed to load email settings:', err);
      setError('Failed to load email settings');
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.put('/email-settings', settings);
      setSuccess(response.data.message);
      loadSettings();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save email settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    setLoading(true);
    setResult(null);
    setError('');

    try {
      const response = await api.post('/test/email', testEmail);
      setResult(response.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send test email');
      setResult(err.response?.data);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, color: accentBlue, fontWeight: 600 }}>Email Setup</Typography>

      <Paper sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
            },
            '& .Mui-selected': {
              color: `${navyBlue} !important`,
            },
            '& .MuiTabs-indicator': {
              backgroundColor: navyBlue,
            },
          }}
        >
          <Tab icon={<SettingsIcon />} label="Email Configuration" iconPosition="start" />
          <Tab icon={<SendIcon />} label="Send Test Email" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Configuration Tab */}
      {tabValue === 0 && (
        <Paper sx={{ p: 3, borderRadius: 2, border: `1px solid ${borderColor}` }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          {/* Provider Selection */}
          <Typography variant="h6" sx={{ mb: 2, color: accentBlue, fontWeight: 600 }}>
            Email Provider
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
            <Chip
              label="SMTP (Microsoft 365, Gmail, etc.)"
              onClick={() => setSettings({ ...settings, provider: 'smtp' })}
              sx={{
                fontWeight: 600,
                backgroundColor: settings.provider === 'smtp' ? navyBlue : 'transparent',
                color: settings.provider === 'smtp' ? 'white' : navyBlue,
                border: `1.5px solid ${navyBlue}`,
                '&:hover': {
                  backgroundColor: settings.provider === 'smtp' ? '#2c4a6e' : 'rgba(30, 58, 95, 0.08)',
                },
              }}
            />
            <Chip
              label="Resend (Cloud/Railway)"
              onClick={() => setSettings({ ...settings, provider: 'resend' })}
              sx={{
                fontWeight: 600,
                backgroundColor: settings.provider === 'resend' ? navyBlue : 'transparent',
                color: settings.provider === 'resend' ? 'white' : navyBlue,
                border: `1.5px solid ${navyBlue}`,
                '&:hover': {
                  backgroundColor: settings.provider === 'resend' ? '#2c4a6e' : 'rgba(30, 58, 95, 0.08)',
                },
              }}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* SMTP Configuration */}
          {settings.provider === 'smtp' && (
            <>
              <Typography variant="h6" sx={{ mb: 3, color: accentBlue, fontWeight: 600 }}>
                SMTP Configuration
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    label="SMTP Host"
                    value={settings.host}
                    onChange={(e) => setSettings({ ...settings, host: e.target.value })}
                    helperText="e.g., smtp.office365.com or smtp.gmail.com"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="SMTP Port"
                    type="number"
                    value={settings.port}
                    onChange={(e) => setSettings({ ...settings, port: e.target.value })}
                    helperText="Usually 587"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email Address (Username)"
                    value={settings.user}
                    onChange={(e) => setSettings({ ...settings, user: e.target.value })}
                    helperText="The email address used to send emails"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email Password"
                    type={showPassword ? 'text' : 'password'}
                    value={settings.password}
                    onChange={(e) => setSettings({ ...settings, password: e.target.value })}
                    helperText="Leave as ******** to keep current password unchanged"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="From Name (Display Name)"
                    value={settings.fromName}
                    onChange={(e) => setSettings({ ...settings, fromName: e.target.value })}
                    helperText="The name that appears as the sender"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>

              <Paper sx={{ p: 2, mt: 3, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Note:</strong> SMTP may not work on cloud platforms like Railway due to port restrictions. Use Resend for cloud deployments.
                </Typography>
              </Paper>
            </>
          )}

          {/* Resend Configuration */}
          {settings.provider === 'resend' && (
            <>
              <Typography variant="h6" sx={{ mb: 3, color: accentBlue, fontWeight: 600 }}>
                Resend Configuration
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Resend API Key"
                    type={showPassword ? 'text' : 'password'}
                    value={settings.resendApiKey}
                    onChange={(e) => setSettings({ ...settings, resendApiKey: e.target.value })}
                    helperText="Get your API key from resend.com"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="From Address"
                    value={settings.resendFrom}
                    onChange={(e) => setSettings({ ...settings, resendFrom: e.target.value })}
                    helperText="e.g., 'Sleeptronic Maintenance <onboarding@resend.dev>' or your verified domain"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>

              <Paper sx={{ p: 2, mt: 3, backgroundColor: tableRowHover, borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Tip:</strong> Resend works great on Railway and other cloud platforms. To use your own domain, verify it in your Resend dashboard.
                </Typography>
              </Paper>
            </>
          )}

          <Divider sx={{ my: 3 }} />

          {/* General Settings */}
          <Typography variant="h6" sx={{ mb: 3, color: accentBlue, fontWeight: 600 }}>
            General Settings
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Application URL"
                value={settings.appUrl}
                onChange={(e) => setSettings({ ...settings, appUrl: e.target.value })}
                helperText="The base URL of this application (used in email links)"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveSettings}
                disabled={saving}
                size="large"
                sx={{
                  background: gradientPrimary,
                  borderRadius: 2,
                  textTransform: 'none',
                  '&:hover': {
                    background: gradientPrimaryHover,
                  },
                }}
              >
                {saving ? 'Saving...' : 'Save Email Settings'}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Test Email Tab */}
      {tabValue === 1 && (
        <>
          <Paper sx={{ p: 3, mb: 3, borderRadius: 2, border: `1px solid ${borderColor}` }}>
            <Typography variant="h6" sx={{ mb: 2, color: accentBlue, fontWeight: 600 }}>Send Test Email</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Recipient Email (leave blank to send to yourself)"
                  value={testEmail.to}
                  onChange={(e) => setTestEmail({ ...testEmail, to: e.target.value })}
                  helperText="Leave blank to send to your own email address"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Subject"
                  value={testEmail.subject}
                  onChange={(e) => setTestEmail({ ...testEmail, subject: e.target.value })}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Message (leave blank for default test message)"
                  value={testEmail.message}
                  onChange={(e) => setTestEmail({ ...testEmail, message: e.target.value })}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={handleTestEmail}
                  disabled={loading}
                  sx={{
                    background: gradientPrimary,
                    borderRadius: 2,
                    textTransform: 'none',
                    '&:hover': {
                      background: gradientPrimaryHover,
                    },
                  }}
                >
                  {loading ? 'Sending...' : 'Send Test Email'}
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Error:</Typography>
              <Typography variant="body2">{error}</Typography>
              {result?.details && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2"><strong>Error Code:</strong> {result.details.code}</Typography>
                  <Typography variant="body2"><strong>Command:</strong> {result.details.command}</Typography>
                  {result.details.response && (
                    <Typography variant="body2"><strong>Server Response:</strong> {result.details.response}</Typography>
                  )}
                </Box>
              )}
            </Alert>
          )}

          {result?.success && (
            <Alert severity="success">
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Success!</Typography>
              <Typography variant="body2">Email sent successfully to: {result.sentTo}</Typography>
              <Typography variant="body2">Message ID: {result.messageId}</Typography>
            </Alert>
          )}

          <Paper sx={{ p: 3, borderRadius: 2, border: `1px solid ${borderColor}` }}>
            <Typography variant="h6" sx={{ mb: 2, color: accentBlue, fontWeight: 600 }}>Troubleshooting Tips</Typography>
            <Typography variant="body2" paragraph>
              <strong>Common Issues:</strong>
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 3 }}>
              <li>
                <Typography variant="body2">
                  <strong>Authentication Failed:</strong> Check that the email address and password are correct
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Connection Timeout:</strong> Verify that port 587 is not blocked by firewall
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Invalid Recipient:</strong> Make sure the recipient email address is valid
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Microsoft 365 Specific:</strong> Ensure the account has SMTP authentication enabled and isn't blocked by security policies
                </Typography>
              </li>
            </Box>
          </Paper>
        </>
      )}
    </Box>
  );
}
